import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import moment from "moment";
import { CONSTANT } from "src/app/constant";
import { Roles } from "src/app/models/enum";
import { StaffDetails } from "src/app/models/models";

/** Cabin Crew form looks up Cabin Crew; Cabin Crew Instructor form looks up Cabin Crew Instructor. */
export function staffLookupRolesForForm(formType: string | null | undefined): string | undefined {
	const code = (formType ?? "").trim().toLowerCase();
	if (code === CONSTANT.FORM_TYPE.cabin_crew) {
		return Roles.CABIN_CREW;
	}
	if (code === CONSTANT.FORM_TYPE.cabin_crew_instructor) {
		return Roles.CABIN_CREW_INSTRUCTOR;
	}
	return undefined;
}

/** Validator added after a failed staff-number API lookup; survives updateValueAndValidity(). */
export const staffNotFoundValidator: ValidatorFn = (): ValidationErrors | null => ({ invalidStaffId: true });

/** Validator when the entered staff number is the logged-in instructor's own. */
export const ownStaffNumberValidator: ValidatorFn = (): ValidationErrors | null => ({ ownStaffNumber: true });

export function normalizeStaffNumber(value: unknown): string {
	return String(value ?? "").trim();
}

/** True when both values are non-empty and match after trimming. */
export function isInstructorOwnStaffNumber(entered: unknown, ownStaffNumber: unknown): boolean {
	const candidate = normalizeStaffNumber(entered);
	const own = normalizeStaffNumber(ownStaffNumber);
	return candidate.length > 0 && own.length > 0 && candidate === own;
}

/** Clears a looked-up candidate and marks the staff field when it is the instructor's own number. */
export function rejectInstructorOwnStaffNumber(
	candidateForm: { get(name: string): AbstractControl | null },
	ownStaffNumber: unknown,
): boolean {
	const staff = candidateForm.get("staffNumber");
	if (!staff || !isInstructorOwnStaffNumber(staff.value, ownStaffNumber)) return false;

	for (const name of ["name", "rank", "pilotProfile", "licenseNo", "licenseType"]) {
		candidateForm.get(name)?.patchValue("", { emitEvent: false });
	}
	candidateForm.get("licenseExpiryDate")?.patchValue(null, { emitEvent: false });
	applyOwnStaffNumberFailure(staff);
	return true;
}

export function applyOwnStaffNumberFailure(control: AbstractControl): void {
	clearStaffLookupFailure(control, staffNotFoundValidator);
	applyStaffLookupFailure(control, ownStaffNumberValidator);
}

export function clearOwnStaffNumberFailure(control: AbstractControl): void {
	clearStaffLookupFailure(control, ownStaffNumberValidator);
}

export function applyStaffLookupFailure(
	control: AbstractControl,
	validator: ValidatorFn = staffNotFoundValidator,
): void {
	if (!control.hasValidator(validator)) {
		control.addValidators(validator);
	}
	control.markAsTouched();
	control.updateValueAndValidity({ emitEvent: false });
}

export function clearStaffLookupFailure(
	control: AbstractControl,
	validator: ValidatorFn = staffNotFoundValidator,
): void {
	if (control.hasValidator(validator)) {
		control.removeValidators(validator);
		control.updateValueAndValidity({ emitEvent: false });
	}
}

/** Clears lookup-failed and self-rating state when the user edits the staff number below 7 digits. */
export function clearStaffLookupFailureOnValueChange(
	control: AbstractControl,
	validator: ValidatorFn = staffNotFoundValidator,
): void {
	const value = (control.value ?? "").toString();
	if (value.length >= 7) return;
	if (control.hasValidator(validator)) {
		clearStaffLookupFailure(control, validator);
	}
	clearOwnStaffNumberFailure(control);
}

/** Parses API values such as `2027-04-30T00:00:00.000Z` for form/datepicker use. */
export function parseStaffLicenseExpiryDate(raw: string | Date | null | undefined): Date | null {
	if (!raw) return null;
	if (raw instanceof Date) return isNaN(+raw) ? null : raw;

	const m = moment(raw);
	return m.isValid() ? m.toDate() : null;
}

export function patchStaffLicenseExpiryDate(
	control: AbstractControl,
	raw: string | Date | null | undefined,
	opts?: { emitEvent?: boolean },
): void {
	const date = parseStaffLicenseExpiryDate(raw);
	if (!date) return;
	control.patchValue(date, { emitEvent: opts?.emitEvent ?? false });
}

export function isStaffLookupResponseValid(resp: unknown): resp is { data: StaffDetails } {
	if (!resp || typeof resp !== "object") return false;
	const data = (resp as { data?: StaffDetails }).data;
	if (!data || typeof data !== "object") return false;
	const name = (data.name ?? "").toString().trim();
	const staffNumber = (data.staffNumber ?? "").toString().trim();
	return name.length > 0 || staffNumber.length > 0;
}

export function isStaffLookupErrorPayload(resp: unknown): boolean {
	if (resp == null) return true;
	if (typeof resp === "string") {
		const lower = resp.toLowerCase();
		return (
			lower.includes("no profile found") ||
			lower.includes("not found") ||
			lower.includes("http failure")
		);
	}
	if (typeof resp === "object") {
		const r = resp as Record<string, unknown>;
		const nestedError = r["error"];
		const errors = Array.isArray(r["errors"]) ? r["errors"].map(String).join(" ") : "";
		const msg = [
			r["message"],
			errors,
			nestedError,
			typeof nestedError === "object" ? (nestedError as Record<string, unknown>)["message"] : null,
		]
			.filter((m): m is string => typeof m === "string")
			.join(" ")
			.toLowerCase();
		if (msg.includes("no profile found") || msg.includes("not found")) return true;
		const status = (r["statusCode"] as number | undefined) ?? (r["status"] as number | undefined);
		if (status === 404 || status === 500) return true;
	}
	return false;
}
